import * as React from "react";

import * as Redux from "react-redux";
import { useLocation } from "react-router-dom";

import { isOnStatusOrOnboardingRoute } from "client/domain/Onboarding/Faqs/Faqs";
import { useFeatureFlags } from "client/hooks/useFeatureFlags";
import { TotalAnnualSales } from "client/lib/apt-merchant";

export type ContactUsSource = "footer" | "faq";

type ProviderType = {
  contactUsOpen: ContactUsSource | null;
  setContactUsOpen: (source: ContactUsSource | null) => void;
  setContactUsClose: () => void;
};

type Context = ProviderType & {
  showContactUs: boolean;
};

const defaultValue = {
  contactUsOpen: null,
  setContactUsOpen: () => {
    // do nothing
  },
  setContactUsClose: () => {
    // do nothing
  },
};

const ContactUsContext = React.createContext<ProviderType>(defaultValue);

export const showContactUsCta = (
  isOnApplicableRoute: boolean,
  featureFlagUnder100k: boolean,
  featureFlagOver100k: boolean,
  merchantPortalReleaseEnableContactUsFooter: boolean,
  sourceLocation: ContactUsSource | null,
  totalAnnualSales?: TotalAnnualSales,
  opportunityOwnerEmail?: string
): boolean => {
  // only show on onboarding and status pages
  if (!isOnApplicableRoute) {
    return false;
  }

  // check if neither feature flag is true
  if (!featureFlagUnder100k && !featureFlagOver100k) {
    return false;
  }

  // footer contact us needs it's own feature flag
  if (
    sourceLocation === "footer" &&
    !merchantPortalReleaseEnableContactUsFooter
  ) {
    return false;
  }

  // for now were only showing to merchants with < 100k total annual sales
  const under100kAnnualSales: TotalAnnualSales[] = [
    "$0-$100K",
    "Under $100,000",
    "Up to £100k",
  ];

  // if a merchant has TAS < 100k and feature flag is on
  if (
    featureFlagUnder100k &&
    totalAnnualSales &&
    under100kAnnualSales.includes(totalAnnualSales)
  ) {
    return true;
  }

  // if a merchant has TAS > 100k, feature flag is on, and they have an opp owner email
  if (
    featureFlagOver100k &&
    totalAnnualSales &&
    !under100kAnnualSales.includes(totalAnnualSales) &&
    opportunityOwnerEmail
  ) {
    return true;
  }

  // any other cases should not show the button
  return false;
};

// holds logic for open/close of which source
const ContactUsProvider: React.FC = ({ children }) => {
  const [
    contactUsOpen,
    setContactUsOpen,
  ] = React.useState<ContactUsSource | null>(null);

  const value = {
    contactUsOpen,
    setContactUsOpen,
    setContactUsClose: () => setContactUsOpen(null),
  };
  return (
    <ContactUsContext.Provider value={value}>
      {children}
    </ContactUsContext.Provider>
  );
};

// holds logic responsible for showing the contact us button
const useContactUs = (contactUsSource: ContactUsSource = "faq"): Context => {
  const context = React.useContext(ContactUsContext);
  if (context === undefined) {
    throw new Error("useContactUs must be used within a ContactUsProvider");
  }
  const [showContactUs, setShowContactUs] = React.useState<boolean>(false);
  const location = useLocation();
  const isCorrectRoute = isOnStatusOrOnboardingRoute(location.pathname);
  const {
    merchantAccount: { opportunityOwnerEmail },
    fieldValueMap,
  } = Redux.useSelector(({ merchant }: any) => merchant);
  const [flags] = useFeatureFlags();

  React.useEffect(() => {
    // only show contact us modal if TAS>100k and opportunityOwnerEmail exists or simply <100k TAS
    const tas =
      fieldValueMap.storeDetails.totalAnnualSales ||
      fieldValueMap.lead.totalAnnualSales;
    const show = showContactUsCta(
      isCorrectRoute,
      flags.merchantPortalReleaseEnableContactUsModal,
      flags.merchantPortalReleaseEnableContactUsModalOver100K,
      flags.merchantPortalReleaseEnableContactUsFooter,
      contactUsSource,
      tas,
      opportunityOwnerEmail
    );
    setShowContactUs(show);
  }, [
    location.pathname,
    flags.merchantPortalReleaseEnableContactUsModalOver100K,
    flags.merchantPortalReleaseEnableContactUsModal,
    flags.merchantPortalReleaseEnableContactUsFooter,
    opportunityOwnerEmail,
    fieldValueMap.storeDetails.totalAnnualSales,
    fieldValueMap.lead.totalAnnualSales,
    contactUsSource,
  ]);

  return { ...context, showContactUs };
};

export { ContactUsProvider, useContactUs };
